import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Slider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { ColorLens, FontDownload, Palette } from '@mui/icons-material';

// Import all resume templates
import ModernTemplate from '../templates/ModernTemplate';
import ClassicTemplate from '../templates/ClassicTemplate';
import CreativeTemplate from '../templates/CreativeTemplate';
import MinimalTemplate from '../templates/MinimalTemplate';
import ProfessionalTemplate from '../templates/ProfessionalTemplate';

/**
 * Customize Style Tab Component
 * Handles resume template selection and style customization
 */
const CustomizeStyleTab = ({
  selectedTemplate,
  setSelectedTemplate,
  templates,
  customizations,
  updateCustomization,
  showTemplateSelector,
  setShowTemplateSelector
}) => {
  return (
    <Grid container spacing={3}>
      {/* Template Selection */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Resume Templates
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Choose from our professionally designed templates
          </Typography>
          
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={template.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTemplate === template.id ? 2 : 1,
                    borderColor: selectedTemplate === template.id ? 'primary.main' : 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => setSelectedTemplate(template.id)}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      height: 200,
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    {/* Template Preview Placeholder */}
                    <Box
                      sx={{
                        width: '80%',
                        height: '90%',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1
                      }}
                    >
                      <Box sx={{ height: '20%', backgroundColor: '#e3f2fd', mb: 0.5, borderRadius: 0.5 }} />
                      <Box sx={{ height: '15%', backgroundColor: '#f5f5f5', mb: 0.5, borderRadius: 0.5 }} />
                      <Box sx={{ height: '30%', backgroundColor: '#f9f9f9', mb: 0.5, borderRadius: 0.5 }} />
                      <Box sx={{ height: '35%', backgroundColor: '#fafafa', borderRadius: 0.5 }} />
                    </Box>
                    
                    {selectedTemplate === template.id && (
                      <Chip
                        label="Selected"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8
                        }}
                      />
                    )}
                  </CardMedia>
                  <CardContent>
                    <Typography variant="h6" align="center">
                      {template.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>

      {/* Color Customization */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <ColorLens sx={{ mr: 1, verticalAlign: 'middle' }} />
            Color Scheme
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Primary Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {[
                '#2196f3', '#4caf50', '#ff9800', '#f44336', 
                '#9c27b0', '#607d8b', '#795548', '#424242'
              ].map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: customizations.primaryColor === color ? 3 : 1,
                    borderColor: customizations.primaryColor === color ? 'primary.main' : 'divider'
                  }}
                  onClick={() => updateCustomization('primaryColor', color)}
                />
              ))}
            </Box>
            
            <TextField
              label="Custom Primary Color"
              type="color"
              value={customizations.primaryColor}
              onChange={(e) => updateCustomization('primaryColor', e.target.value)}
              sx={{ width: 100 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Secondary Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {[
                '#f5f5f5', '#e8f5e8', '#fff3e0', '#ffebee', 
                '#f3e5f5', '#eceff1', '#efebe9', '#fafafa'
              ].map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: customizations.secondaryColor === color ? 3 : 1,
                    borderColor: customizations.secondaryColor === color ? 'primary.main' : 'divider'
                  }}
                  onClick={() => updateCustomization('secondaryColor', color)}
                />
              ))}
            </Box>
            
            <TextField
              label="Custom Secondary Color"
              type="color"
              value={customizations.secondaryColor}
              onChange={(e) => updateCustomization('secondaryColor', e.target.value)}
              sx={{ width: 100 }}
            />
          </Box>
        </Paper>
      </Grid>

      {/* Typography Customization */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <FontDownload sx={{ mr: 1, verticalAlign: 'middle' }} />
            Typography
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Font Family</InputLabel>
            <Select
              value={customizations.fontFamily}
              onChange={(e) => updateCustomization('fontFamily', e.target.value)}
              label="Font Family"
            >
              <MenuItem value="Arial">Arial</MenuItem>
              <MenuItem value="Helvetica">Helvetica</MenuItem>
              <MenuItem value="Georgia">Georgia</MenuItem>
              <MenuItem value="Times New Roman">Times New Roman</MenuItem>
              <MenuItem value="Calibri">Calibri</MenuItem>
              <MenuItem value="Roboto">Roboto</MenuItem>
              <MenuItem value="Open Sans">Open Sans</MenuItem>
              <MenuItem value="Lato">Lato</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="subtitle2" gutterBottom>
            Font Size: {customizations.fontSize}px
          </Typography>
          <Slider
            value={customizations.fontSize}
            onChange={(e, value) => updateCustomization('fontSize', value)}
            min={10}
            max={18}
            step={1}
            marks
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth>
            <InputLabel>Spacing</InputLabel>
            <Select
              value={customizations.spacing}
              onChange={(e) => updateCustomization('spacing', e.target.value)}
              label="Spacing"
            >
              <MenuItem value="compact">Compact</MenuItem>
              <MenuItem value="normal">Normal</MenuItem>
              <MenuItem value="relaxed">Relaxed</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      </Grid>

      {/* Layout Options */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            <Palette sx={{ mr: 1, verticalAlign: 'middle' }} />
            Layout Options
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1">Single Column</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Traditional layout
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1">Two Column</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Modern sidebar layout
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1">Header Focus</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Prominent header design
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1">Timeline</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Chronological layout
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      {/* Preview Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Style Preview
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            See how your customizations look
          </Typography>
          
          <Box
            sx={{
              minHeight: 200,
              backgroundColor: customizations.secondaryColor,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              fontFamily: customizations.fontFamily,
              fontSize: customizations.fontSize
            }}
          >
            <Box
              sx={{
                backgroundColor: customizations.primaryColor,
                color: 'white',
                p: 2,
                borderRadius: 1,
                mb: 2
              }}
            >
              <Typography variant="h6" sx={{ fontFamily: customizations.fontFamily }}>
                John Doe
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: customizations.fontFamily }}>
                Software Engineer
              </Typography>
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                fontFamily: customizations.fontFamily,
                fontSize: customizations.fontSize,
                lineHeight: customizations.spacing === 'compact' ? 1.2 : 
                          customizations.spacing === 'relaxed' ? 1.8 : 1.5
              }}
            >
              This is a preview of how your resume will look with the current style settings.
              The colors, fonts, and spacing will be applied to your actual resume content.
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default CustomizeStyleTab;
